import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryDetailService from '../../../app/services/hooks/useDeliveryDetailService';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useBatchService from '../../../app/services/hooks/useBatchService';
import useSalesOrderDetailService from '../../../app/services/hooks/useSalesOrderDetailService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { DeliveryDetailDataForm, defaultDeliveryDetailDataForm } from '../../../app/models/DeliveryDetail';
import { Batch } from '../../../app/models/Batch';
import { SalesOrderDetail } from '../../../app/models/SalesOrderDetail';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import DeliveryDetailForm from './forms/DeliveryDetailForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';

interface Props {
    deliveryId: number;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const DeliveryDetailCreate = ({ deliveryId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    
    const { storeDeliveryDetail, fetchingStoreDeliveryDetail } = useDeliveryDetailService();
    const { showDelivery, fetchingShowDelivery } = useDeliveryService();
    const { getBatches, fetchingGetBatches } = useBatchService();
    const { getSalesOrderDetails, fetchingGetSalesOrderDetails } = useSalesOrderDetailService();

    // Estados
    const [detail, setDetail] = useState<DeliveryDetailDataForm>({
        ...defaultDeliveryDetailDataForm,
        delivery_id: deliveryId
    });
    const [errorFields, setErrorFields] = useState<any>();
    const [batches, setBatches] = useState<Batch[]>([]);
    const [salesOrderDetails, setSalesOrderDetails] = useState<SalesOrderDetail[]>([]);
    const [salesOrderId, setSalesOrderId] = useState<number | null>(null);
    // Lotes preparados para guardar en esta posición
    const [pendingRows, setPendingRows] = useState<Array<{ batch_id: number; quantity: number }>>([]);

    useEffect(() => {
        loadDelivery();
    }, [deliveryId]);

    useEffect(() => {
        if (salesOrderId) {
            loadSalesOrderDetails();
            loadBatches();
        }
    }, [salesOrderId]);

    const loadDelivery = () => {
        showDelivery(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                const delivery = response.data.delivery;
                console.log('Delivery data:', delivery);
                console.log('sales_order_id:', delivery.sales_order_id);
                
                // Intentar obtener sales_order_id de diferentes formas
                const orderId = delivery.sales_order_id || 
                               (delivery.sales_order && delivery.sales_order.id) || 
                               null;
                
                console.log('Final sales_order_id:', orderId);
                
                if (orderId) {
                    setSalesOrderId(orderId);
                } else {
                    console.error('No se encontró sales_order_id en la entrega');
                    toast.error('No se pudo obtener el pedido asociado a la entrega');
                }
            },
            onError: (error) => {
                console.error('Error al cargar entrega:', error);
                toast.error('Error al cargar la entrega');
            }
        });
    };

    const loadSalesOrderDetails = () => {
        if (!salesOrderId) {
            console.log('No hay salesOrderId, no se pueden cargar las posiciones');
            return;
        }
        
        console.log('Cargando posiciones del pedido:', salesOrderId);
        
        getSalesOrderDetails(
            {
                page: 1,
                per_page: 1000,
                sort: 'position',
                order: 'asc',
                sales_order_id: salesOrderId
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    console.log('Posiciones del pedido recibidas:', response.data?.sales_order_details);
                    setSalesOrderDetails(response.data?.sales_order_details || []);
                },
                onError: (error) => {
                    console.error('Error al cargar posiciones del pedido:', error);
                    toast.error('Error al cargar las posiciones del pedido');
                }
            }
        );
    };

    const loadBatches = () => {
        // Solo cargar lotes si hay una posición seleccionada con producto
        if (!detail.sales_order_detail_id) {
            console.log('No hay sales_order_detail_id seleccionado');
            setBatches([]);
            return;
        }

        const selectedDetail = salesOrderDetails.find(d => d.id === detail.sales_order_detail_id);
        console.log('Selected detail:', selectedDetail);
        console.log('Product ID:', selectedDetail?.product_id);
        
        if (!selectedDetail) {
            console.log('No se encontró el detalle seleccionado');
            setBatches([]);
            return;
        }
        
        if (!selectedDetail.product_id) {
            console.log('El detalle seleccionado no tiene product_id');
            setBatches([]);
            return;
        }

        console.log('Cargando lotes para product_id:', selectedDetail.product_id);

        getBatches(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'desc',
                product_id: selectedDetail.product_id
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    console.log('Lotes recibidos:', response.data?.batches);
                    console.log('Total lotes:', response.data?.batches?.length || 0);
                    setBatches(response.data?.batches || []);
                },
                onError: (error) => {
                    console.error('Error al cargar lotes:', error);
                    toast.error('Error al cargar los lotes disponibles');
                }
            }
        );
    };

    // Recargar lotes cuando cambia la posición seleccionada
    useEffect(() => {
        if (detail.sales_order_detail_id && salesOrderDetails.length > 0) {
            loadBatches();
        }
    }, [detail.sales_order_detail_id]);
    /**
     * Valida la fila actual (detail.batch_id / detail.quantity) y,
     * si es correcta, la agrega a la lista de lotes pendientes.
     */
    const addRow = () => {
        // Validar que se haya seleccionado una posición del pedido
        if (!detail.sales_order_detail_id) {
            setErrorFields({ sales_order_detail_id: ['Debe seleccionar una posición del pedido'] });
            return;
        }

        // Validar que se haya seleccionado un lote
        if (!detail.batch_id) {
            setErrorFields({ batch_id: ['Debe seleccionar un lote'] });
            return;
        }

        // Validar cantidad básica
        if (!detail.quantity || Number(detail.quantity) <= 0) {
            setErrorFields({ quantity: ['La cantidad debe ser mayor a 0'] });
            return;
        }

        const selectedBatch = batches.find(b => b.id === detail.batch_id);
        const selectedOrderDetail = salesOrderDetails.find(d => d.id === detail.sales_order_detail_id);

        // Validar que la unidad de la posición del pedido coincida con la unidad del lote
        if (selectedBatch && selectedOrderDetail) {
            const orderUnit = (selectedOrderDetail.quantity_unit || '').toString().trim().toLowerCase();
            const batchUnit = (selectedBatch.unit || '').toString().trim().toLowerCase();
            if (orderUnit && batchUnit && orderUnit !== batchUnit) {
                useReactConfirmAlert().errorAlert({
                    title: 'Unidades distintas',
                    message: `La unidad de la posición del pedido (${selectedOrderDetail.quantity_unit || 'N/A'}) no coincide con la unidad del lote (${selectedBatch.unit || 'N/A'}). Debe seleccionar un lote con la misma unidad que la posición del pedido.`
                });
                return;
            }
        }

        // Validación: la cantidad no puede exceder la cantidad del lote
        if (selectedBatch) {
            const batchQuantity = Number(selectedBatch.quantity) || 0;
            const requestedQuantity = Number(detail.quantity) || 0;
            if (requestedQuantity > batchQuantity) {
                setErrorFields({
                    quantity: [`La cantidad no puede exceder la cantidad del lote (${batchQuantity} ${selectedBatch.unit || ''})`]
                });
                return;
            }
        }

        // Agregar a la lista de lotes pendientes
        setPendingRows(prev => [
            ...prev,
            { batch_id: detail.batch_id as number, quantity: Number(detail.quantity) || 0 }
        ]);

        // Limpiar lote y cantidad para permitir seguir "pistoleando"
        setDetail(prev => ({
            ...prev,
            batch_id: undefined,
            quantity: 0
        }));
    };

    const store = () => {
        // Si no hay lotes acumulados, usamos la fila actual como un solo lote (modo antiguo)
        const rowsToSend =
            pendingRows.length > 0
                ? pendingRows
                : detail.batch_id && detail.quantity && Number(detail.quantity) > 0
                    ? [{ batch_id: detail.batch_id as number, quantity: Number(detail.quantity) }]
                    : [];

        if (!detail.sales_order_detail_id) {
            setErrorFields({ sales_order_detail_id: ['Debe seleccionar una posición del pedido'] });
            return;
        }

        if (rowsToSend.length === 0) {
            setErrorFields({ batch_id: ['Debe agregar al menos un lote'] });
            return;
        }

        if (showLoading) showLoading('loading', 'Guardando detalle...');

        const basePayload: DeliveryDetailDataForm = {
            ...detail,
            delivery_id: deliveryId
        };

        const sendNext = (index: number) => {
            if (index >= rowsToSend.length) {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: 'Lotes agregados correctamente a la entrega.'
                });

                toast.success('Lotes agregados correctamente a la entrega.', { autoClose: 2500 });

                if (onSaved) onSaved();
                return;
            }

            const row = rowsToSend[index];
            const payload: DeliveryDetailDataForm = {
                ...basePayload,
                batch_id: row.batch_id,
                quantity: row.quantity
            };

            storeDeliveryDetail(deliveryId, payload, {
                onSuccess: () => {
                    sendNext(index + 1);
                },
                onError: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();

                    useReactConfirmAlert().errorAlert({
                        title: 'Error',
                        message: response.message
                    });
                },
                onFieldError: (fieldErrors: ServiceResponse) => {
                    if (hideLoading) hideLoading();
                    setErrorFields(fieldErrors.data);
                }
            });
        };

        sendNext(0);
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (fetchingShowDelivery || fetchingGetSalesOrderDetails || fetchingGetBatches) {
        return <LazyLoading height={300} />;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <DeliveryDetailForm
                        detail={detail}
                        setDetail={setDetail}
                        batches={batches}
                        salesOrderDetails={salesOrderDetails}
                        errorFields={errorFields}
                        pendingRows={pendingRows}
                        onAddRow={addRow}
                        onRemoveRow={(index) =>
                            setPendingRows(prev => prev.filter((_, i) => i !== index))
                        }
                        onChangePendingRow={(index, patch) =>
                            setPendingRows(prev =>
                                prev.map((row, i) => {
                                    if (i !== index) return row;
                                    const updated = { ...row, ...patch };

                                    // Si cambia el lote, heredar cantidad disponible de ese lote
                                    if (patch.batch_id) {
                                        const batch = batches.find(b => b.id === patch.batch_id);
                                        const qty = batch ? Number(batch.quantity) || 0 : 0;
                                        updated.quantity = qty;
                                    }

                                    return updated;
                                })
                            )
                        }
                    />
                </div>
            </div>
            
            <hr />
            
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm 
                        callbackCancel={cancel} 
                        locked={fetchingStoreDeliveryDetail} 
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm 
                        callbackSave={store} 
                        locked={fetchingStoreDeliveryDetail} 
                    />
                </div>
            </div>
        </>
    );
};

export default DeliveryDetailCreate;
